// ============================================================
// KEDBYTE SCHOOLOS — page content.
// CLAIMS POLICY (same Part 9 standard as payroll.js): present
// tense states only what is built and running TODAY — the data
// model, the security boundaries and the administrator
// interface. Fees, attendance, examinations and the parent
// portal are IN PROGRESS and are only ever described that way.
// Never: a launch date, pricing, student/school/user counts,
// competitor comparisons, testimonials or named schools, or
// certifications not held. When in doubt, the weaker claim wins.
// ============================================================

export const features = [
  {
    title: "Built on Indian law, not adapted to it",
    desc: "The DPDP Act 2023 and its 2025 Rules, the RTE Act 2009 and the CBSE Affiliation Bye-Laws were read directly, and they shape the data model itself — child-data protection is the schema, not a settings checkbox.",
  },
  {
    title: "No record without a lawful basis",
    desc: "A child's record cannot exist without a recorded lawful basis under the DPDP Act — enforced by the database, not by policy.",
  },
  {
    title: "One school's data cannot reach another's",
    desc: "Every record is sealed to its school at the database level, and that isolation is tested from both directions on every table before any release.",
  },
  {
    title: "It tells you what it does not know",
    desc: "Missing data is a stated, real state — never an invented value. Every guarantee on screen is displayed at the width it actually holds.",
  },
  {
    title: "Ages are never stored",
    desc: "Age is derived from date of birth each time it is needed, so child protections never lapse by accident.",
  },
];

// How the built core behaves for Indian schools — properties of the data
// model, access scoping and interface that exist today.
export const builtForIndia = [
  {
    title: "The academic year, as schools run it",
    desc: "Sessions run April to March — 2026–27, not a fiscal-year setting bent into shape.",
  },
  {
    title: "Indian notation throughout",
    desc: "Amounts in Indian grouping — ₹1,40,000.00, never ₹140,000.00 — and dates written unambiguously: 5 April 2026.",
  },
  {
    title: "Multi-campus groups",
    desc: "School groups run multiple campuses in one system, with staff access scoped to their posting.",
  },
  {
    title: "Your school's identity",
    desc: "White-label crest and colours — the system carries the school's identity, not ours.",
  },
];

export const compliance = {
  intro:
    "SchoolOS is not adapted to Indian law after the fact — it is built on it. The legislation below was read directly, and it shapes the data model itself.",
  sources: [
    "Digital Personal Data Protection Act, 2023",
    "Digital Personal Data Protection Rules, 2025",
    "Right of Children to Free and Compulsory Education Act, 2009",
    "CBSE Affiliation Bye-Laws",
  ],
  builder: "Built by Kedbyte Technologies Pvt Ltd — the same engineering standard as our other products.",
  status: "In active development.",
};

// Where it stands — stated plainly. `built` is running today; `inProgress`
// is not available yet and must never be described as if it were.
export const status = {
  built: [
    {
      title: "The data model",
      desc: "Student and staff records carrying the guarantees above — lawful basis, school sealing, derived ages.",
    },
    {
      title: "The security boundaries",
      desc: "School-level isolation, tested from both directions on every table before any release.",
    },
    {
      title: "The administrator interface",
      desc: "The surface where school administrators create and manage records.",
    },
  ],
  inProgress: [
    { title: "Fees", desc: "Being built now — not yet available." },
    { title: "Attendance", desc: "Being built now — not yet available." },
    { title: "Examinations", desc: "Being built now — not yet available." },
    { title: "Parent portal", desc: "Being built now — not yet available." },
  ],
  note: "We're working with a small number of schools during development.",
};
